import { MomentInput } from "moment";
import { Bar } from "PFApp/booking/types";
import { useAccordion } from "PFComponents/accordion/use_accordion";
import { ActionIcon } from "PFComponents/action_icon";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Typography } from "PFComponents/typography";
import { DEFAULT_DATE_FORMAT } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Profile } from "PFTypes";
import React, { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import { useOverbookingColor } from "../../../warning_icons/use_overbooking_color";
import { BookingItem } from "./booking_item";
import css from "./overbooking_detail.module.scss";
import { OverbookingsRange } from "./use_overbooking_ranges";

type OverbookingProps = PropsWithChildren<
  OverbookingsRange & {
    profile: Profile;
    bars?: Bar[][];
    scrollToDate: (date: string) => void;
    onDeleted: (entity: any) => Promise<void>;
    onEdited: () => void;
  }
>;
type OverbookingAccordionProps = Omit<OverbookingProps, "bookings" | "profile" | "onDeleted" | "onEdited">;

const OverbookingAccordion = ({
  start,
  end,
  overbookings,
  scrollToDate,
  children
}: OverbookingAccordionProps) => {
  const { utc } = useDateFormatter();
  const { t } = useTranslation("bookingModule", { keyPrefix: "details.overbookings" });
  const { overbookingColor } = useOverbookingColor({ overbookings });
  const { handleStateChange, isOpen, contentProps } = useAccordion({
    defaultState: "closed"
  });

  const isOneDay = start === end;

  const formatDate = (date: MomentInput) => utc(date).format(DEFAULT_DATE_FORMAT);

  const avgOverbookedHoursPerDay = -Math.ceil((overbookings[0].available_minutes || 0) / 60);

  return (
    <div className={css.root}>
      <div className={css.header}>
        <span className={css.headerContent}>
          <span className={css.center}>
            <ActionIcon
              name={isOpen ? "chevron-down" : "chevron-right"}
              onClick={handleStateChange}
              title={t(isOpen ? "collapseTooltip" : "showTooltip")}
              size="sm"
            />
            <Icon name="warning" fill={overbookingColor?.hex()} />
            <Typography variant="labelBold" noMargin>
              {isOneDay ? formatDate(start) : `${formatDate(start)} - ${formatDate(end)}`}
            </Typography>
          </span>
          <Button kind="text" small onClick={() => scrollToDate(start)}>
            {t("show")}
          </Button>
        </span>
        <div>{t("hoursOverPerDay", { count: avgOverbookedHoursPerDay })}</div>
      </div>
      <div {...contentProps}>{children}</div>
    </div>
  );
};

export const Overbooking = ({
  start,
  end,
  bookings,
  profile,
  bars,
  overbookings,
  scrollToDate,
  onDeleted,
  onEdited
}: OverbookingProps) => (
  <OverbookingAccordion start={start} end={end} overbookings={overbookings} scrollToDate={scrollToDate}>
    {bookings.map(({ id }) => (
      <BookingItem
        key={id}
        bookingId={id}
        profile={profile}
        onDeleted={onDeleted}
        onEdited={onEdited}
        bars={bars}
      />
    ))}
  </OverbookingAccordion>
);
